import type { RateLineListQuery } from '@chukua/common';
import { Pagination } from 'antd';
import { SearchX, ShieldAlert, Tags } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import type { PagedListState } from '../../../../../shared/hooks/use-paged-list';
import { LaneList } from './LaneList';
import type { RateLineView } from '../../../rate-cards.api';
import { todayIso } from '../../../policies/rate-line-status';

interface LinesResultsProps {
  state: PagedListState<RateLineView>;
  isRefreshing: boolean;
  list: ListQueryControls<RateLineListQuery>;
  isCurrent: boolean;
  canEdit: boolean;
  onChangePrice: (line: RateLineView) => void;
  onShowHistory: (line: RateLineView) => void;
}

function emptyTitle(isFiltered: boolean, isCurrent: boolean): string {
  if (isFiltered) return 'No matching lines';
  return isCurrent ? 'Nothing in force today' : 'No superseded lines';
}

/** The lines area: skeleton on first load, then this page grouped by lane, with the old rows kept (dimmed) while the next page loads. */
export function LinesResults({ state, isRefreshing, list, isCurrent, canEdit, onChangePrice, onShowHistory }: LinesResultsProps) {
  if (state.status === 'loading') return <RowListSkeleton label="Loading rate lines" />;
  if (state.status !== 'loaded') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load the lines" description="Something went wrong loading this card's lines. Try reloading the page." />
    );
  }

  const { page } = state;
  if (page.total === 0) {
    return (
      <EmptyState
        icon={list.isFiltered ? SearchX : Tags}
        title={emptyTitle(list.isFiltered, isCurrent)}
        description={list.isFiltered ? 'No lines match these filters.' : 'Lines you add appear here once they are approved and start.'}
      />
    );
  }

  return (
    <div className={isRefreshing ? 'opacity-60' : undefined}>
      <LaneList lines={page.items} today={todayIso()} canEdit={canEdit} onChangePrice={onChangePrice} onShowHistory={onShowHistory} />
      <Pagination
        className="mt-4 justify-end"
        current={page.page}
        pageSize={page.pageSize}
        total={page.total}
        showSizeChanger
        showTotal={(total) => `${total} total`}
        onChange={(nextPage, pageSize) => list.setQuery({ page: nextPage, pageSize })}
      />
    </div>
  );
}
